import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import FlowLayout from '../FlowLayout';
import { useA11yTokens } from '../../hooks/useA11yTokens';
import { DeliveryMethodTypes } from '../../types/promotion';
import { PhoneOutlineIcon, WhatsAppOutlineIcon } from '../../icons';
import { salesWhatsappNumber } from '../../constants';
import { setFlowToken, setPhone } from '../../store/mainSlice';
import { useDispatch } from 'react-redux';
import { useIsDesktopDevice } from '../../hooks/useIsDesktopDevice';

const SUCCESS_GREEN = '#16a34a';

const OfferSuccess: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const theme4dTokens = useA11yTokens();
    const isDesktop = useIsDesktopDevice();
    const navigate = useNavigate();
    const { phone, selectedContactToken, customersContacts, packageInfo, sentOffer, packageToken, env } = useAppSelector(
        (state) => state.main,
    );
    const selectedContact = customersContacts.find((c) => c.contactToken === selectedContactToken);
    const whatsappHref = `https://wa.me/${salesWhatsappNumber}?text=${encodeURIComponent(
        t('offer.whatsappMessage', { promoTitle: packageInfo?.promoTitle ?? '' }),
    )}`;

    React.useEffect(() => {
        if (!sentOffer) {
            dispatch(setPhone(''));
            dispatch(setFlowToken(''));
            navigate(packageToken ? `/?token=${encodeURIComponent(packageToken)}&ENV=${env}` : '/');
        }
    }, []);

    const onBackToStart = () => {
        dispatch(setPhone(''));
        dispatch(setFlowToken(''));
        navigate(packageToken ? `/?token=${encodeURIComponent(packageToken)}&ENV=${env}` : '/');
    }

    if (!sentOffer) return null;

    const isEmail = sentOffer.method === DeliveryMethodTypes.Email;

    return (
        <FlowLayout>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', pt: '6px', textAlign: 'center', ...(isDesktop && { mt: '50px' }) }}>
                <Box
                    sx={{
                        width: 76,
                        height: 76,
                        borderRadius: '999px',
                        bgcolor: SUCCESS_GREEN,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: '4px',
                    }}
                >
                    <CheckIcon sx={{ fontSize: 38, color: '#ffffff' }} />
                </Box>
                <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 23 : 21, fontWeight: 700, color: theme4dTokens.text, m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>
                    {t('offer.sentTitle')}
                </Typography>
                <Typography sx={{ fontSize: isDesktop ? 16 : 14, color: theme4dTokens.muted, lineHeight: 1.6, m: 0, wordBreak: 'break-word' }}>
                    {isEmail
                        ? t('offer.sentSubjectEmail', { customer: selectedContact?.customerName })
                        : t('offer.sentSubjectSms', { customer: selectedContact?.customerName })}
                </Typography>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    alignSelf: 'center',
                    gap: '8px',
                    width: 'fit-content',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                    bgcolor: theme4dTokens.surface,
                    border: `1px solid ${theme4dTokens.border}`,
                    borderRadius: theme4dTokens.radius,
                    p: '12px 16px',
                    ...(isDesktop && { mt: '20px' }),
                }}
            >
                {isEmail ? (
                    <MailOutlineIcon sx={{ fontSize: 18, color: theme4dTokens.accent, flexShrink: 0 }} />
                ) : (
                    <PhoneIphoneIcon sx={{ fontSize: 18, color: theme4dTokens.accent, flexShrink: 0 }} />
                )}
                <Typography sx={{ fontSize: isDesktop ? 17 : 15, fontWeight: 700, color: theme4dTokens.text, direction: 'ltr', wordBreak: 'break-word' }}>
                    {isEmail ? selectedContact?.contactEmail : phone}
                </Typography>
            </Box>

            <Typography sx={{ fontSize: isDesktop ? 18 : 12.5, fontWeight: '700', color: theme4dTokens.muted, textAlign: 'center', m: 0, wordBreak: 'break-word', ...(isDesktop && { mt: '16px' }) }}>
                {t('offer.ticketNumber', { id: sentOffer.addressingId })}
            </Typography>

            <Box sx={{ bgcolor: theme4dTokens.surface, border: `1px solid ${theme4dTokens.border}`, borderRadius: theme4dTokens.radius, p: '16px', display: 'flex', flexDirection: 'column', gap: '12px', ...(isDesktop && { mt: '20px' }) }}>
                <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 15.5 : 13.5, fontWeight: 700, color: theme4dTokens.muted, textAlign: 'center', m: 0, wordBreak: 'break-word' }}>
                    {t('offer.supportTitle')}
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '10px' }}>
                    {/* <Box
                        component="a"
                        href="tel:031234567"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '3px',
                            p: '13px 10px',
                            bgcolor: theme4dTokens.bg,
                            border: `1px solid ${theme4dTokens.border}`,
                            borderRadius: theme4dTokens.radius,
                            textDecoration: 'none',
                            color: theme4dTokens.text,
                            '&:hover': { borderColor: theme4dTokens.accent },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                borderRadius: '999px',
                                bgcolor: theme4dTokens.accentSoft,
                                color: theme4dTokens.accent,
                                mb: '3px',
                            }}
                        >
                            <PhoneOutlineIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: 14, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' }}>
                            {t('errors.callSupportLabel')}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: theme4dTokens.muted, direction: 'ltr', wordBreak: 'break-word', textAlign: 'center' }}>03-123-4567</Typography>
                    </Box> */}
                    <Box
                        component="a"
                        href={whatsappHref}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '3px',
                            p: '13px 10px',
                            bgcolor: theme4dTokens.bg,
                            border: `1px solid ${theme4dTokens.border}`,
                            borderRadius: theme4dTokens.radius,
                            textDecoration: 'none',
                            color: theme4dTokens.text,
                            '&:hover': { borderColor: '#25D366' },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 34,
                                height: 34,
                                borderRadius: '999px',
                                bgcolor: 'rgba(37,211,102,.14)',
                                color: '#1aa04e',
                                mb: '3px',
                            }}
                        >
                            <WhatsAppOutlineIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography sx={{ fontFamily: theme4dTokens.headFont, fontSize: isDesktop ? 16 : 14, fontWeight: 700, textAlign: 'center', wordBreak: 'break-word' }}>WhatsApp</Typography>
                        <Typography sx={{ fontSize: isDesktop ? 14 : 12, color: theme4dTokens.muted, textAlign: 'center', wordBreak: 'break-word' }}>{t('errors.whatsappSub')}</Typography>
                    </Box>
                </Box>
            </Box>

            {/* <Typography sx={{ fontSize: 12.5, color: theme4dTokens.muted, textAlign: 'center', lineHeight: 1.6, m: 0, wordBreak: 'break-word' }}>
                {t('offer.contactSalesPrefix')}{' '}
                <Box
                    component="a"
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener"
                    sx={{ color: theme4dTokens.accent, fontWeight: 700, textDecoration: 'none', wordBreak: 'break-word' }}
                >
                    {t('offer.salesTeam')}
                </Box>
            </Typography> */}

            {/* <Typography
                component="button"
                onClick={onBackToStart}
                sx={{
                    appearance: 'none',
                    width: '100%',
                    fontSize: isDesktop ? 16 : 14,
                    fontWeight: 700,
                    color: theme4dTokens.muted,
                    textAlign: 'center',
                    textDecoration: 'underline',
                    bgcolor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: theme4dTokens.font,
                    p: '6px',
                    mt: 'auto',
                    wordBreak: 'break-word',
                }}
            >
                {t('offer.backToStart')}
            </Typography> */}
        </FlowLayout>
    );
};

export default OfferSuccess;
